import {
  Box,
  Chip,
  Container,
  Link,
  TablePagination,
  Typography,
  useMediaQuery,
} from "@mui/material";
import SuppliesHeader from "./SuppliesHeader/SuppliesHeader";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { FormattedOwnerData } from "../../core/models/ownerData/ownerData.model";
import {
  FiltersChipsSupplies,
  FiltersSupplies,
  GetSuppliesRequestBody,
  SuppliesTableRow,
  TotalSupplies,
} from "../../core/models/supplies/supplies.model";
import { getSupplies } from "../../core/services/supplies/supplies.service";
import {
  formatSuppliesCups,
  getLocalstorageSupplyFilters,
  initialFiltersSupplies,
  removeLocalstorageSupplyFilters,
  setLocalstorageSupplyFilters,
} from "../../utils/components/supplies/supplies.util";
import SuppliesTable from "./SuppliesTable/SuppliesTable";
import { useTranslation } from "react-i18next";
import "./Supplies.scss";
import ButtonElement from "../UI/ButtonElement/ButtonElement";
import SuppliesFilters from "./SuppliesFilters/SuppliesFilters";
import SuppliesCard from "./SuppliesCard/SuppliesCard";
import TotalEnergiesContext from "../../core/store/context/TotalEnergiesContext";
import EmptyStateComponent from "../UI/EmptyStateComponent/EmptyStateComponent";
import LoadingComponentComponent from "../UI/LoadingComponentComponent/LoadingComponentComponent";
import { ROWS_PER_PAGE_OPTIONS } from "../../core/constants/constants";
import { FiltersCifCups } from "../../core/models/filtersCifCups/filtersCifCups.model";

const Supplies = () => {
  const { t } = useTranslation();
  const { ownerData, userData } = useContext(TotalEnergiesContext);
  const savedFilters = getLocalstorageSupplyFilters();
  const isDesktopViewport = useMediaQuery("(min-width:1440px)");
  const [suppliesTableRows, setSuppliesTableRows] = useState<
    SuppliesTableRow[]
  >([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [isFiltersOpen, setIsFiltersOpen] = useState<boolean>(false);
  const [filtersSupplies, setFiltersSupplies] = useState<FiltersSupplies>(
    savedFilters ? JSON.parse(savedFilters!) : initialFiltersSupplies
  );
  const [envirormentSelected, setEnvirormentSelected] =
    useState<string>("light");
  const [filtersChipsSupplies, setFiltersChipsSupplies] = useState<
    FiltersChipsSupplies[]
  >([]);
  const [totalSupplies, setTotalSupplies] = useState<TotalSupplies>({
    totalRegisters: null,
    totalSuppliesElectricity: null,
    totalSuppliesGas: null,
    totalContractElectricity: null,
    totalContractGas: null,
  });
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showButton, setShowButton] = useState(true);
  const showNoResults = useMemo(
    () =>
      !isLoading &&
      ((totalSupplies.totalSuppliesElectricity === 0 &&
        envirormentSelected === "light") ||
        (totalSupplies.totalSuppliesGas === 0 &&
          envirormentSelected === "gas")),
    [
      envirormentSelected,
      isLoading,
      totalSupplies.totalSuppliesElectricity,
      totalSupplies.totalSuppliesGas,
    ]
  );

  const getSuppliesTableRows = useCallback(
    (
      { isAdministrator, idClient, idAdministrator }: FormattedOwnerData,
      token: string,
      { cups, cif, location, socialReason }: FiltersSupplies,
      page: number,
      rowsPerPage: number,
      envirormentSelected: string | null,
      abortSignal: AbortSignal
    ) => {
      const getSuppliesRequestBody: GetSuppliesRequestBody = {
        userid: isAdministrator ? idAdministrator : idClient,
        environmentCode:
          envirormentSelected === null
            ? null
            : envirormentSelected === "light"
            ? "luz"
            : "gas",
        isAdmin: isAdministrator,
        pageSize: rowsPerPage.toString(),
        pageIndex: page.toString(),
        city: location?.length ? location : null,
        cups,
        cif,
        razonSocial: socialReason,
      };

      getSupplies(getSuppliesRequestBody, token, abortSignal)
        .then(
          ({
            totalRegistrosQuery,
            totalSuministrosLuz,
            totalSuministrosGas,
            totalContratosLuz,
            totalContratosGas,
            cups,
          }) => {
            if (envirormentSelected) {
              setTotalSupplies((prevTotalSupplies) => ({
                ...prevTotalSupplies,
                totalRegisters: totalRegistrosQuery,
              }));
              setSuppliesTableRows(formatSuppliesCups(cups));
              return;
            }

            setTotalSupplies((prevTotalSupplies) => ({
              ...(envirormentSelected === null
                ? {
                    totalSuppliesElectricity: totalSuministrosLuz,
                    totalSuppliesGas: totalSuministrosGas,
                    totalContractElectricity: totalContratosLuz,
                    totalContractGas: totalContratosGas,
                  }
                : prevTotalSupplies),
              totalRegisters: totalRegistrosQuery,
            }));
          }
        )
        .catch(() => {})
        .finally(() => {
          setIsLoading(false);
        });
    },
    []
  );

  useEffect(() => {
    const suppliesAbortController = new AbortController();
    const suppliesAbortController2 = new AbortController();

    if (ownerData && userData) {
      window.scrollTo({ top: 0, behavior: "auto" });
      setSuppliesTableRows([]);
      setIsLoading(true);
      getSuppliesTableRows(
        ownerData,
        userData.token,
        filtersSupplies,
        page,
        rowsPerPage,
        envirormentSelected,
        suppliesAbortController.signal
      );
      getSuppliesTableRows(
        ownerData,
        userData.token,
        filtersSupplies,
        page,
        rowsPerPage,
        null,
        suppliesAbortController2.signal
      );
      getFiltersChips(filtersSupplies);

      return () => {
        suppliesAbortController.abort();
        suppliesAbortController2.abort();
      };
    }
  }, [
    envirormentSelected,
    filtersSupplies,
    getSuppliesTableRows,
    ownerData,
    page,
    rowsPerPage,
    userData,
  ]);

  useEffect(() => {
    const handleScroll = () => {
      const isAtBottom =
        window.innerHeight + window.scrollY >= document.body.scrollHeight;

      if (isAtBottom && !isDesktopViewport) {
        setShowButton(false);
      } else {
        setShowButton(true);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [isDesktopViewport]);

  const getFiltersChips = (filtersSupplies: FiltersSupplies) => {
    setFiltersChipsSupplies([]);
    const formattedFiltersSupplies = {
      ...filtersSupplies,
      location: filtersSupplies.location?.length
        ? [filtersSupplies.location]
        : [],
    };

    for (const [key, values] of Object.entries(formattedFiltersSupplies)) {
      if (values.length) {
        setFiltersChipsSupplies((prevFiltersChipsSupplies) => [
          ...prevFiltersChipsSupplies,
          {
            filterSuppliesKey: key as keyof FiltersCifCups,
            totalValues: values.length,
            label: values[0],
          },
        ]);
      }
    }
  };

  const handleDeleteChip = (filterSuppliesKey: keyof FiltersCifCups) => {
    const newSuppliesFilters = {
      ...filtersSupplies,
      [filterSuppliesKey]: [],
    };

    setLocalstorageSupplyFilters(newSuppliesFilters);

    setFiltersSupplies(newSuppliesFilters);

    setFiltersChipsSupplies((prevFiltersChipsSupplies) =>
      prevFiltersChipsSupplies.filter(
        ({ filterSuppliesKey: prevFilterSuppliesKey }) =>
          prevFilterSuppliesKey !== filterSuppliesKey
      )
    );
  };

  const showPagination =
    Math.ceil(totalSupplies.totalRegisters || NaN / rowsPerPage) !== 1 &&
    (totalSupplies.totalRegisters || NaN) >= rowsPerPage &&
    !isLoading;

  return (
    <Box component="section" className="supplies">
      <SuppliesHeader
        ownerData={ownerData}
        isDesktopViewport={isDesktopViewport}
        totalSupplies={totalSupplies}
        envirormentSelected={envirormentSelected}
        setEnvirormentSelected={setEnvirormentSelected}
      />
      <Container
        maxWidth="xl"
        component="article"
        className="supplies__table-filters-container"
      >
        <Box
          component="div"
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={3}
          paddingBottom="1rem"
        >
          <Box component="div" flexBasis="100%">
            {showButton && (
              <ButtonElement
                onClick={() => setIsFiltersOpen(true)}
                secondary
                iconL
                iconName="t-icon-filter"
                disabled={
                  (!!!suppliesTableRows.length &&
                    !!!filtersChipsSupplies.length) ||
                  (!!!filtersChipsSupplies.length && showNoResults)
                }
              >
                {t("Tablas.Filtros")}{" "}
                {!!filtersChipsSupplies.length && (
                  <span className="supplies__filter-count">
                    {filtersChipsSupplies.length}
                  </span>
                )}
              </ButtonElement>
            )}
            {!!filtersChipsSupplies.length &&
              filtersChipsSupplies.map(
                ({ filterSuppliesKey, label, totalValues }) => (
                  <Chip
                    className="filter-chip"
                    key={filterSuppliesKey}
                    label={
                      <Typography>
                        {label}{" "}
                        {totalValues > 1 && (
                          <span className="supplies__filter-count">
                            +{totalValues - 1}
                          </span>
                        )}
                      </Typography>
                    }
                    onDelete={() => {
                      handleDeleteChip(filterSuppliesKey);
                      // setPage(0);
                      // refreshLocalstorageSupplyFilters(filtersSupplies);
                    }}
                  />
                )
              )}
            {!!filtersChipsSupplies.length && (
              <Link
                component="button"
                onClick={() => {
                  setFiltersChipsSupplies([]);
                  setFiltersSupplies(initialFiltersSupplies);
                  removeLocalstorageSupplyFilters();
                  setPage(0);
                }}
              >
                {t("Tablas.Borrar todo")}
              </Link>
            )}
          </Box>
          <SuppliesFilters
            isFiltersOpen={isFiltersOpen}
            setIsFiltersOpen={setIsFiltersOpen}
            filtersSupplies={filtersSupplies}
            setFiltersSupplies={(filters) => {
              setLocalstorageSupplyFilters(filters);
              setFiltersSupplies(filters);
              setPage(0);
            }}
            ownerDataCups={ownerData?.cups || []}
          />
        </Box>
      </Container>
      {!showNoResults && (
        <Container maxWidth="xl" component="article">
          {isDesktopViewport ? (
            <SuppliesTable
              suppliesTableRows={suppliesTableRows}
              isAdministrator={ownerData?.isAdministrator}
            />
          ) : (
            <SuppliesCard
              suppliesTableRows={suppliesTableRows}
              isAdministrator={ownerData?.isAdministrator}
            />
          )}
          {isLoading && (
            <Box padding={"2rem 0"}>
              <LoadingComponentComponent
                isLoading={isLoading}
                backgroundColor={"transparent"}
                position={"relative"}
                height={"4rem"}
              />
            </Box>
          )}
          {showPagination && (
            <TablePagination
              className="table-pagination"
              showFirstButton
              showLastButton
              component="div"
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              labelRowsPerPage={t(`Tablas.Filas por página`)}
              labelDisplayedRows={({ page, count }) =>
                t("Tablas.Página de", {
                  actual: page + 1,
                  total: Math.ceil(count / rowsPerPage),
                })
              }
              count={totalSupplies.totalRegisters || NaN}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={(
                event: React.MouseEvent<HTMLButtonElement> | null,
                newPage: number
              ) => {
                setPage(newPage);
              }}
              onRowsPerPageChange={(
                event: React.ChangeEvent<HTMLInputElement>
              ) => {
                setRowsPerPage(+event.target.value);
                setPage(0);
              }}
            />
          )}
        </Container>
      )}
      {showNoResults && (
        <Container maxWidth="xl" component="article">
          <EmptyStateComponent text={"No hay resultados"} height="17rem" />
        </Container>
      )}
    </Box>
  );
};

export default Supplies;
